<!-- components/form/TextControl.vue -->
<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'
import FieldControl from './FieldControl.vue'
import InputShell from './InputShell.vue'

defineOptions({ inheritAttrs: false })

type ValidateOn = 'input' | 'blur' | 'submit'
type TextType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'search'
  | 'tel'
  | 'url'

interface ExtendedAttrs {
  maxlength?: string | number
  'max-length'?: string | number
  maxLength?: string | number
  [key: string]: unknown
}

const props = withDefaults(
  defineProps<{
    /** Nome do campo no VeeValidate (obrigatório) */
    name: string
    /** Label flutuante dentro do campo */
    label?: string
    required?: boolean
    hint?: string
    /** v-model externo (opcional) */
    modelValue?: unknown
    /** Estratégia de validação */
    validateOn?: ValidateOn
    /** Atributos do input */
    type?: TextType
    placeholder?: string
    clearable?: boolean
    forId?: string
    /** Transforms do FieldControl */
    mask?: (v: unknown) => unknown
    normalize?: (v: unknown) => unknown
    /** Estado */
    debounce?: number
    disabled?: boolean
    readonly?: boolean
    /** Regras locais do VeeValidate */
    rules?: unknown
    /** Exibir contador de caracteres (usa maxlength dos attrs) */
    showCounter?: boolean
  }>(),
  {
    label: '',
    required: false,
    hint: '',
    modelValue: undefined,
    validateOn: undefined,
    type: 'text',
    placeholder: '',
    clearable: false,
    forId: undefined,
    mask: undefined,
    normalize: undefined,
    debounce: 0,
    disabled: false,
    readonly: false,
    rules: undefined,
    showCounter: false,
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: unknown): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'enter'): void
  (e: 'clear'): void
}>()

const attrs = useAttrs() as ExtendedAttrs
const { t } = useI18n({ useScope: 'global' })
const isFocused = ref(false)

/** ID estável para acessibilidade e testes */
const controlId = computed(() => props.forId ?? `tc-${props.name}`)

/** Traduz mensagens de erro do vee-validate */
function translateError(e: unknown): string | string[] | null {
  if (!e) return null
  if (Array.isArray(e)) {
    const out = e
      .map((msg) => (typeof msg === 'string' ? t(msg) : String(msg)))
      .filter((msg) => msg && msg.length > 0)
    return out.length ? out : null
  }
  return typeof e === 'string' ? t(e) : String(e)
}

/** Eventos padrão */
function onFocus() {
  isFocused.value = true
  emit('focus')
}
function onBlur() {
  isFocused.value = false
  emit('blur')
}
function onEnter() {
  emit('enter')
}
function onClear() {
  emit('clear')
}

const maxLength = computed<number | undefined>(() => {
  const raw =
    attrs.maxlength ?? attrs['max-length'] ?? attrs.maxLength ?? undefined
  if (raw === undefined || raw === null) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
})

/** Mostra contador se showCounter=true OU se houver maxlength nos attrs */
const shouldShowCounter = computed<boolean>(() => {
  return props.showCounter || maxLength.value !== undefined
})
</script>

<template>
  <FieldControl
    :name="props.name"
    :model-value="props.modelValue"
    :validate-on="props.validateOn"
    :normalize="props.normalize"
    :mask="props.mask"
    :debounce="props.debounce"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :rules="props.rules"
    @update:modelValue="(v) => emit('update:modelValue', v)"
    @focus="onFocus"
    @blur="onBlur"
    @enter="onEnter"
  >
    <template #default="{ bind, error }">
      <InputShell
        :label="''"
        :required="props.required"
        :hint="props.hint"
        :error="null"
        :for-id="controlId"
        :disabled="props.disabled"
        :readonly="props.readonly"
      >
        <template #control>
          <v-text-field
            v-bind="{ ...bind, ...attrs }"
            :id="controlId"
            :type="props.type"
            :placeholder="props.placeholder"
            :clearable="props.clearable"
            variant="outlined"
            :label="props.label"
            :error="!!error"
            :error-messages="translateError(error) ?? []"
            :messages="[]"
            :hide-details="!shouldShowCounter && !error"
            :counter="shouldShowCounter ? (maxLength ?? true) : false"
            :persistent-counter="shouldShowCounter"
            :maxlength="maxLength"
            :input-props="{ id: controlId }"
            @focus="onFocus"
            @blur="onBlur"
            @keydown.enter.prevent="onEnter"
            @click:clear="onClear"
          >
            <!-- slots opcionais -->
            <template v-if="$slots.prepend" #prepend>
              <slot name="prepend" />
            </template>
            <template v-if="$slots.append" #append>
              <slot name="append" />
            </template>
            <template v-if="$slots['prepend-inner']" #prepend-inner>
              <slot name="prepend-inner" />
            </template>
            <template v-if="$slots['append-inner']" #append-inner>
              <slot name="append-inner" />
            </template>
          </v-text-field>
        </template>

        <!-- slots do InputShell -->
        <template v-if="$slots['label-append']" #label-append>
          <slot name="label-append" />
        </template>
        <template v-if="$slots.hint" #hint>
          <slot name="hint" />
        </template>
        <template v-if="$slots.actions" #actions>
          <slot name="actions" />
        </template>
      </InputShell>
    </template>
  </FieldControl>
</template>
